/**
 * Centralized Authentication Context for Consumer Portal
 *
 * This context manages authentication state using centralized Auth Portal (Service 22)
 * for all authentication flows.
 *
 * Flow:
 * 1. User visits Consumer Portal without auth → redirects to Auth Portal
 * 2. Auth Portal handles login (Firebase) → exchanges for JWT
 * 3. Auth Portal redirects back with ?token=<jwt>
 * 4. Consumer Portal extracts token, stores in localStorage, cleans URL
 * 5. Subsequent requests use JWT from localStorage
 *
 * Security:
 * - JWT signature verification happens server-side in API Gateway
 * - Open redirect attacks prevented via URL validation
 * - Tokens are immediately cleaned from URL after extraction
 */

import { ConsumerType } from '@ops-matrix/shared-types';
import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { env } from '../config/env';
import {
  clearTokens,
  getAccessToken as getStoredAccessToken,
  setAccessToken as storeAccessToken,
} from '../lib/auth/storage';
import { getDeviceFingerprint } from '../utils/deviceFingerprint';

// Re-export ConsumerType for backward compatibility
export { ConsumerType } from '@ops-matrix/shared-types';

// ============================================================================
// Auth Utilities (inlined for ESM compatibility)
// ============================================================================

/**
 * Impersonation context from JWT (Issue #321)
 * Used when admin/business user is viewing as this consumer
 */
interface ImpersonationContext {
  actorId: string;
  actorEmail: string;
  actorRole: string;
  actorBusinessId?: string;
  reason: string;
  startedAt: number;
  expiresAt: number;
  sessionId: string;
}

interface ConsumerJWTPayload {
  sub: string;
  email: string;
  name: string;
  role: string;
  businessId: string;
  phone?: string;
  tenantId?: string;
  realmId?: string; // Issue #314: Add realmId for multi-tenancy
  consumerType?: ConsumerType;
  exp: number;
  iat: number;
  // Issue #321: Impersonation context
  impersonation?: ImpersonationContext;
}

function parseJWT(token: string): ConsumerJWTPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.warn('[Auth] Invalid JWT format');
      return null;
    }
    const base64Url = parts[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join(''),
    );
    return JSON.parse(jsonPayload) as ConsumerJWTPayload;
  } catch (error) {
    console.error('[Auth] Failed to parse JWT:', error);
    return null;
  }
}

function isTokenExpired(token: string, bufferSeconds = 30): boolean {
  const payload = parseJWT(token);
  if (!payload?.exp) {
    console.warn('[Auth] Token has no expiration claim');
    return true;
  }
  return Date.now() >= (payload.exp - bufferSeconds) * 1000;
}

function extractConsumerFromToken(token: string): Consumer | null {
  const payload = parseJWT(token);
  if (!payload) {
    console.error('[Auth] Failed to extract consumer from token');
    return null;
  }

  // Issue #321: Extract impersonation context if present
  const isImpersonating = !!payload.impersonation;
  const impersonationContext = payload.impersonation;

  // Derive consumerType from JWT; partners should use the Business Portal
  // so we default to END_CONSUMER if the type is not a consumer type
  const derivedConsumerType = payload.consumerType ?? ConsumerType.END_CONSUMER;

  const consumer: Consumer = {
    id: payload.sub,
    email: payload.email,
    name: payload.name,
    businessId: payload.businessId,
    consumerType: derivedConsumerType,
    isImpersonating,
    impersonationContext,
  };
  if (payload.tenantId) {
    consumer.tenantId = payload.tenantId;
  }
  if (payload.realmId) {
    consumer.realmId = payload.realmId;
  }
  if (payload.phone) {
    consumer.phone = payload.phone;
  }
  return consumer;
}

function cleanTokenFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    let urlModified = false;
    if (url.searchParams.has('token')) {
      url.searchParams.delete('token');
      urlModified = true;
    }
    // Issue #321: Also clean impersonation_token
    if (url.searchParams.has('impersonation_token')) {
      url.searchParams.delete('impersonation_token');
      urlModified = true;
    }
    if (urlModified) {
      window.history.replaceState({}, '', url.toString());
    }
  } catch (error) {
    console.error('[Auth] Failed to clean URL:', error);
  }
}

// Allowed redirect URL patterns to prevent open redirect attacks
// Using restrictive patterns: [a-z0-9-]+ for single-level subdomains only
const ALLOWED_REDIRECT_PATTERNS = [
  /^https:\/\/portal\.[a-z0-9-]+\.com$/i,
  /^https:\/\/[a-z0-9-]+-portal\.[a-z0-9-]+\.com$/i,
  /^http:\/\/localhost:\d+$/,
];

function validateRedirectUrl(redirectUrl: string | null | undefined, currentOrigin: string): string | null {
  if (!redirectUrl) return null;

  // Block protocol-relative URLs (e.g., //evil.com) before URL parsing
  if (redirectUrl.startsWith('//')) {
    console.warn('[Auth] Blocked protocol-relative URL:', redirectUrl);
    return null;
  }

  try {
    const url = new URL(redirectUrl);
    if (url.origin === currentOrigin) return redirectUrl;

    const isAllowed = ALLOWED_REDIRECT_PATTERNS.some((pattern) => pattern.test(url.origin));
    if (isAllowed) return redirectUrl;

    console.warn(`[Auth] Blocked redirect to untrusted origin: ${url.origin}`);
    return null;
  } catch {
    // Only allow relative paths starting with single slash
    if (redirectUrl.startsWith('/')) {
      return `${currentOrigin}${redirectUrl}`;
    }
    console.warn(`[Auth] Invalid redirect URL format: ${redirectUrl}`);
    return null;
  }
}

function getSafeRedirectUrl(redirectUrl: string | null | undefined, currentOrigin: string, fallbackPath = '/'): string {
  return validateRedirectUrl(redirectUrl, currentOrigin) || `${currentOrigin}${fallbackPath}`;
}

// ============================================================================
// Auth Context
// ============================================================================

interface Consumer {
  id: string;
  email: string;
  name: string;
  businessId: string;
  tenantId?: string;
  realmId?: string; // Issue #314: Add realmId for multi-tenancy
  phone?: string;
  consumerType: ConsumerType;
  // Issue #321: Impersonation context
  isImpersonating?: boolean;
  impersonationContext?: ImpersonationContext;
}

interface ConsumerAuthContextValue {
  consumer: Consumer | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => Promise<void>;
  getAccessToken: () => string | null;
  redirectToLogin: (tenantId?: string, returnPath?: string, mode?: 'login' | 'register') => void;
  // Issue #321: Impersonation support
  // Note: Consumers cannot end impersonation sessions (only actors can)
  isImpersonating: boolean;
  impersonationContext: ImpersonationContext | undefined;
}

const ConsumerAuthContext = createContext<ConsumerAuthContextValue | undefined>(undefined);

/**
 * Auth Portal URL - centralized authentication service
 */
const AUTH_PORTAL_URL = env.VITE_AUTH_PORTAL_URL;

export function useConsumerAuth() {
  const context = useContext(ConsumerAuthContext);
  if (!context) {
    throw new Error('useConsumerAuth must be used within ConsumerAuthProvider');
  }
  return context;
}

interface ConsumerAuthProviderProps {
  children: React.ReactNode;
}

export function ConsumerAuthProvider({ children }: ConsumerAuthProviderProps) {
  const [consumer, setConsumer] = useState<Consumer | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Initialize auth state from URL token or localStorage
   */
  useEffect(() => {
    const initializeAuth = () => {
      try {
        // Check for token in URL (coming back from Auth Portal)
        const urlParams = new URLSearchParams(window.location.search);
        const tokenFromUrl = urlParams.get('token');
        // Issue #321: Check for impersonation token from Admin/Business Portal
        const impersonationToken = urlParams.get('impersonation_token');

        // Priority: impersonation_token > regular token
        const activeToken = impersonationToken || tokenFromUrl;

        if (activeToken) {
          // Immediately clean tokens from URL for security
          cleanTokenFromUrl();

          // Validate and store the token
          if (!isTokenExpired(activeToken)) {
            storeAccessToken(activeToken);

            // Extract consumer from token
            const extractedConsumer = extractConsumerFromToken(activeToken);
            if (extractedConsumer) {
              setConsumer(extractedConsumer);
            } else {
              console.error('[Auth] Failed to parse token from URL');
            }
            setIsLoading(false);
            return;
          }
          console.warn('[Auth] Token from Auth Portal is expired');
        }

        // Check localStorage for existing token
        const storedToken = getStoredAccessToken();

        if (storedToken && !isTokenExpired(storedToken)) {
          const extractedConsumer = extractConsumerFromToken(storedToken);
          if (extractedConsumer) {
            setConsumer(extractedConsumer);
          } else {
            // Token is malformed, clear it
            console.error('[Auth] Failed to parse stored token');
            clearTokens();
          }
        } else if (storedToken) {
          // Token expired, clear it
          clearTokens();
        }
      } catch (err) {
        console.error('[Auth] Initialization error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  /**
   * Logout - revoke push token, clear tokens, and redirect to Auth Portal
   */
  const logout = useCallback(async () => {
    // Capture token before clearing — needed for push token revocation
    const token = getStoredAccessToken();

    // Clear local tokens IMMEDIATELY for security (before any async work)
    clearTokens();

    // Get tenant ID from current consumer or URL (before clearing state)
    const tenantId = consumer?.tenantId || new URLSearchParams(window.location.search).get('tenant') || '';

    // Clear consumer state
    setConsumer(null);

    // Revoke push notification token (fire-and-forget, uses captured token)
    if (token) {
      try {
        await fetch(`${env.VITE_API_URL}/trpc/pushNotifications.revokeToken?batch=1`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            '0': {
              json: {
                deviceFingerprint: getDeviceFingerprint(),
              },
            },
          }),
        });
      } catch {
        // Don't block logout on push token revocation failure
      }
    }

    // Build safe redirect URL for after logout
    const safeRedirect = getSafeRedirectUrl(window.location.origin, window.location.origin, '/');

    // Redirect to Auth Portal logout
    window.location.href = `${AUTH_PORTAL_URL}/logout?portal=consumer&tenant=${tenantId}&redirect=${encodeURIComponent(safeRedirect)}`;
  }, [consumer]);

  /**
   * Get current access token
   */
  const getAccessToken = useCallback((): string | null => {
    const token = getStoredAccessToken();
    if (token && !isTokenExpired(token)) {
      return token;
    }
    return null;
  }, []);

  /**
   * Redirect to Auth Portal for login with open redirect protection
   *
   * Forwards important URL params (leadId, ref, businessId) as top-level params
   * to Auth Portal so they can be used for form pre-fill during registration.
   */
  const redirectToLogin = useCallback(
    (tenantId?: string, returnPath?: string, mode: 'login' | 'register' = 'login') => {
      const origin = window.location.origin;
      const currentPath = window.location.pathname + window.location.search;

      // Use safe redirect URL to prevent open redirect attacks
      const redirectUrl = getSafeRedirectUrl(returnPath || currentPath, origin, '/');

      // Extract important params from current URL to forward to Auth Portal
      const currentParams = new URLSearchParams(window.location.search);
      const tenant = tenantId || currentParams.get('tenant') || '';
      const leadId = currentParams.get('leadId') || '';
      const ref = currentParams.get('ref') || '';
      // businessId can be 'b' (short) or 'businessId' (full) in the invite URL
      const businessId = currentParams.get('businessId') || currentParams.get('b') || '';

      // Build params for Auth Portal - forward leadId, ref, businessId as top-level params
      // so Auth Portal can use them for form pre-fill without parsing the redirect URL
      const params = new URLSearchParams({
        portal: 'consumer',
        tenant,
        redirect: redirectUrl,
      });

      // Add optional params if present
      if (leadId) params.set('leadId', leadId);
      if (ref) params.set('ref', ref);
      if (businessId) params.set('businessId', businessId);

      // Use different auth portal route based on mode
      const authRoute = mode === 'register' ? '/register/consumer' : '/login';
      const finalUrl = `${AUTH_PORTAL_URL}${authRoute}?${params.toString()}`;

      window.location.href = finalUrl;
    },
    [],
  );

  // Issue #321: Extract impersonation state from consumer
  const isImpersonating = consumer?.isImpersonating ?? false;
  const impersonationContext = consumer?.impersonationContext;

  const value: ConsumerAuthContextValue = {
    consumer,
    isAuthenticated: !!consumer,
    isLoading,
    logout,
    getAccessToken,
    redirectToLogin,
    isImpersonating,
    impersonationContext,
  };

  return <ConsumerAuthContext.Provider value={value}>{children}</ConsumerAuthContext.Provider>;
}
